import {useCallback, useEffect, useState} from 'react'
import {View} from 'react-native'
import * as Clipboard from 'expo-clipboard'
import {Trans} from '@lingui/macro'

import {useBateskyInvitesQuery} from '#/state/queries/batesky-invites'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Ticket_Stroke2_Corner0_Rounded as TicketIcon} from '#/components/icons/Ticket'
import {Text} from '#/components/Typography'

function InviteCodeRow({
  code,
  isUsed,
  usedByUsername,
}: {
  code: string
  isUsed: boolean
  usedByUsername: string | null
}) {
  const t = useTheme()
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => setCopied(false), 2000)
      return () => clearTimeout(timeout)
    }
  }, [copied])

  const onCopy = useCallback(() => {
    Clipboard.setStringAsync(code)
    setCopied(true)
  }, [code])

  if (isUsed) {
    return (
      <View style={[a.flex_row, a.align_center, a.gap_xs]}>
        <Text
          style={[
            a.flex_1,
            a.text_xs,
            a.font_bold,
            t.atoms.text_contrast_low,
            {textDecorationLine: 'line-through'},
          ]}>
          {code}
        </Text>
        <Text style={[a.text_xs, t.atoms.text_contrast_low]}>
          {usedByUsername || <Trans>Used</Trans>}
        </Text>
      </View>
    )
  }

  return (
    <View style={[a.flex_row, a.align_center, a.gap_xs]}>
      <Text
        style={[
          a.flex_1,
          a.text_xs,
          a.font_bold,
          t.atoms.text_contrast_medium,
        ]}>
        {code}
      </Text>
      <Button
        label={copied ? 'Copied' : 'Copy'}
        onPress={onCopy}
        size="tiny"
        variant="ghost"
        color="primary">
        <ButtonText>
          {copied ? <Trans>Copied!</Trans> : <Trans>Copy</Trans>}
        </ButtonText>
      </Button>
    </View>
  )
}

export function SidebarInviteCodes() {
  const t = useTheme()
  const {data: invites, isLoading} = useBateskyInvitesQuery()

  if (isLoading || !invites || invites.length === 0) {
    return null
  }

  return (
    <View style={[a.gap_sm]}>
      <View style={[a.flex_row, a.align_center, a.gap_xs]}>
        <TicketIcon size="sm" />
        <Text
          style={[
            a.flex_1,
            a.text_sm,
            a.font_bold,
            t.atoms.text_contrast_medium,
          ]}>
          <Trans>Invite Friends</Trans>
        </Text>
      </View>
      <View style={[a.gap_xs]}>
        {invites.map(invite => (
          <InviteCodeRow
            key={invite.code}
            code={invite.code}
            isUsed={invite.isUsed}
            usedByUsername={invite.usedByUsername}
          />
        ))}
      </View>
    </View>
  )
}
